<template>
  <div id="return-summary" class="grid-x margin-top-m">
    <div
      class="cell small-12 return-summary"
      :class="
        isReturnConfirmationPage ? 'medium-12 margin-bottom-s' : replaceAndExchangeOption && page === 'return-details' ? 'medium-4' : 'medium-6'
      "
    >
      <div class="h5 margin-bottom-xs" v-text="summaryTitle"></div>
      <SummaryRow
        v-for="(row, index) in summaryRows"
        :key="row.rowSeq"
        :row="{
          ...row,
          altText: returnDetails?.refundToAccountType,
        }"
        :show-icon="!isReturnConfirmationPage"
        :icon="icon"
        :show-margin-top="index > 0"
        :space-between="isReturnConfirmationPage"
      />
    </div>
    <div v-if="showOrderInformation" id="order-info" class="cell small-12 medium-4">
      <div class="h5 margin-bottom-xs small-margin-top-xs large-margin-top-z">Order Information</div>
      <p class="margin-bottom-xxs">
        <NuxtLink
          class="link-body order-number"
          :to="{
            name: 'Order Details',
            params: { orderNumber },
            query: { o: returnDetails.orderToken },
          }"
          :aria-label="`Order # ${makeNumberAccessible(orderNumber)}`"
        >
          {{ `Order # ${orderNumber}` }}
        </NuxtLink>
      </p>
    </div>
    <div
      v-if="replaceAndExchangeOption && returnDetails.shippingContact"
      id="deliver-address"
      class="cell small-12 medium-4 margin-top-m custom-padding"
    >
      <div class="heavy margin-bottom-xxs">Delivery Address</div>
      <div class="margin-bottom-xxs" v-text="returnDetails.shippingContact.firstName + ' ' + returnDetails.shippingContact.lastName"></div>
      <div
        v-if="returnDetails.shippingContact.address.addressLine1"
        class="margin-bottom-xxs"
        v-text="returnDetails.shippingContact.address.addressLine1"
      ></div>
      <div
        v-if="returnDetails.shippingContact.address.addressLine2"
        class="margin-bottom-xxs"
        v-text="returnDetails.shippingContact.address.addressLine2"
      ></div>
      <div
        v-if="
          returnDetails.shippingContact.address.city ||
          returnDetails.shippingContact.address.state ||
          returnDetails.shippingContact.address.postalCode
        "
        class="margin-bottom-xxs"
      >
        {{ returnDetails.shippingContact.address.city }}
        {{ returnDetails.shippingContact.address.state }}
        {{ returnDetails.shippingContact.address.postalCode }}
      </div>
    </div>
    <div
      v-if="replaceAndExchangeOption && returnDetails.items[0].reasonDescription"
      id="return-reason"
      class="cell small-12 medium-2 margin-top-m custom-padding"
    >
      <div class="heavy margin-bottom-xxs">Return Reason</div>
      <p>
        {{ returnDetails.items[0].reasonDescription }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import SummaryRow from '@/components/common/SummaryRow.vue';
import { formatPrice, makeNumberAccessible } from '@/utils/returns/General';
import estimatedCredit from '@/utils/returns/EstimatedCredit';
import { brandName, brand, RETURN_METHOD_KEY_MAP, RETURN_RESOLUTIONS } from 'config';
import { formatDate } from '@/utils/General';
import { FREE_SHIPMENT, CONDITIONAL_RETURN_FEE_ELIGIBLE_RETURN_METHODS, getCardIcon } from '#shared/constants';

const props = defineProps({
  returnDetails: {
    type: Object,
    required: true,
  },
  page: {
    type: String,
    required: false,
    default: '',
  },
  replaceAndExchangeOption: {
    type: [String, Boolean],
    required: false,
    default: '',
  },
  hideOrderNumber: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const FREE_SHIPMENT_VALUE = FREE_SHIPMENT;
const CONDITIONAL_RETURN_FEE_METHODS = CONDITIONAL_RETURN_FEE_ELIGIBLE_RETURN_METHODS;

const estimatedCreditResponse = computed(() => estimatedCredit(props.returnDetails));
const isReturnConfirmationPage = computed(() => props.page === 'return-confirmation');
const showOrderInformation = computed(() => !(props.hideOrderNumber || isReturnConfirmationPage.value));

const summaryTitle = computed(() => {
  if (isReturnConfirmationPage.value) return 'Return Details';
  if (props.replaceAndExchangeOption)
    return props.replaceAndExchangeOption === RETURN_RESOLUTIONS.REPLACEMENT ? 'Replacement Summary' : 'Exchange Summary';
  return props.returnDetails.returnStatusCode === 303 ? 'Credit Summary' : 'Return Summary';
});

const giftCardText = computed(() => (estimatedCreditResponse.value.eGiftCardMessageFlag ? `${brandName} E-Gift Card` : `${brandName} Gift Card`));

const conditionalReturnFeeEnabled = computed(() => props.returnDetails.conditionalReturnFeeEnabled);

const isMarketplaceOrder = computed(() => props.returnDetails.isMkpOrder);

const orderNumber = computed(() => props.returnDetails.orderNumber);

const icon = computed(() => {
  const type = props.returnDetails.refundToAccountType ?? props.returnDetails.refundMethod;
  const cardIcon = getCardIcon(type);
  if (cardIcon.source === 'link') cardIcon.class = 'card-image';
  return cardIcon;
});

const showRefundToAndTotal = computed(() => {
  const returnStatus = props.returnDetails.returnStatusCode;
  return props.returnDetails.items?.some(
    (item) => item?.returnType === 'PR' || (item?.returnType === 'VR' && [300, 301, 302].includes(returnStatus)),
  );
});

const returnDropOffMethodCheckForShipmentFee = computed(() => CONDITIONAL_RETURN_FEE_METHODS.includes(props.returnDetails.returnDropOffMethod));

const eWasteFee = computed(() => brand === 'mcom' && isMarketplaceOrder.value && props.returnDetails.eWasteFee);

const summaryInitiatedText = (rmaVisibilityChangesEnabled, returnDropOffMethod) =>
  props.page === 'return-details' && rmaVisibilityChangesEnabled && returnDropOffMethod === RETURN_METHOD_KEY_MAP.posReturn
    ? 'Return dropped off on'
    : 'Return Submitted On';
const summaryRows = computed(() => {
  const {
    returnDropOffMethod,
    returnSubmittedDate,
    merchandiseTotal,
    totalTaxes,
    returnStatusCode,
    pendingReturnId,
    reservationNumber,
    rmaVisibilityChangesEnabled,
    protectionPlanTotal,
  } = props.returnDetails || {};

  const {
    originalPaymentFlag,
    refundToMessage,
    refundToAccountNumber,
    refundToAccount,
    giftCardMessageFlag,
    eGiftCardMessageFlag,
    storeCreditMessageFlag,
    refundToOriginalPaymentFlag,
    estimatedCreditMessage,
    estimatedCreditAmount,
  } = estimatedCreditResponse.value;

  const addRow = (condition, row) => (condition ? [row] : []);
  let returnInitiatedText = summaryInitiatedText(rmaVisibilityChangesEnabled, returnDropOffMethod);

  const replaceExchangeSummary = () => {
    returnInitiatedText = props.replaceAndExchangeOption === RETURN_RESOLUTIONS.REPLACEMENT ? 'Replacement submitted on' : 'Exchange submitted on';
    return [
      {
        rowSeq: 1,
        label: returnInitiatedText,
        value: formatDate(returnSubmittedDate),
      },
      {
        rowSeq: 2,
        label: 'Estimated amount due',
        value: formatPrice(props.returnDetails.estimatedRefund),
      },
      ...addRow(orderNumber.value, {
        rowSeq: 3,
        label: 'Order #',
        value: orderNumber.value,
        screenReaderValue: makeNumberAccessible(orderNumber.value),
      }),
      {
        rowSeq: 4,
        label: 'Return ID',
        value: pendingReturnId,
      },
    ];
  };

  if (props.replaceAndExchangeOption) {
    return replaceExchangeSummary();
  }
  return [
    {
      rowSeq: 1,
      label: returnInitiatedText,
      value: formatDate(returnSubmittedDate),
    },
    ...addRow(showRefundToAndTotal.value && originalPaymentFlag, {
      rowSeq: 2,
      label: refundToMessage,
      value: isReturnConfirmationPage.value ? refundToAccount : refundToAccountNumber,
      icon: true,
      screenReaderValue: refundToAccount?.toUpperCase() === 'KLARNA' ? 'KLARNA' : null,
    }),
    ...addRow(showRefundToAndTotal.value && (giftCardMessageFlag || eGiftCardMessageFlag || storeCreditMessageFlag), {
      rowSeq: 3,
      label: storeCreditMessageFlag ? 'Refund as' : 'Refund To',
      value: storeCreditMessageFlag ? 'Store Credit' : giftCardText.value,
      icon: !storeCreditMessageFlag,
    }),
    ...addRow(showRefundToAndTotal.value && refundToOriginalPaymentFlag, {
      rowSeq: 4,
      label: refundToMessage,
      value: estimatedCreditResponse.value.refundToOriginalPayment,
    }),
    {
      rowSeq: 5,
      label: 'Merchandise Total',
      value: formatPrice(merchandiseTotal),
    },
    ...addRow(protectionPlanTotal, {
      rowSeq: 6,
      id: 'protection-plan-fee',
      label: 'Allstate Protection Plan',
      value: formatPrice(protectionPlanTotal),
    }),
    {
      rowSeq: 7,
      label: props.page === 'return-details' ? 'Tax Total' : 'Estimated Tax',
      value: formatPrice(totalTaxes),
    },
    ...addRow(eWasteFee.value, {
      rowSeq: 8,
      id: 'ewaste-fee',
      label: 'eWaste Recycling Fee',
      value: formatPrice(eWasteFee.value),
    }),
    ...addRow(brand === 'mcom' && !isMarketplaceOrder.value && conditionalReturnFeeEnabled.value && returnDropOffMethodCheckForShipmentFee.value, {
      rowSeq: 9,
      id: 'shipping-fee',
      label: returnStatusCode === 303 ? 'Actual Return Shipping Fee' : 'Estimated Return Shipping Fee',
      tooltip: {
        class: 'shipping-fee-tooltip',
        content: `For Star Reward members, return shipping is free. For all other customers, a fee* of $9.99 will be deducted from your refund.<br aria-hidden="true">
            <a href="https://www.macys.com/p/credit-service/benefits/" style="color: white">Learn more</a><br aria-hidden="true"><br aria-hidden="true">* Plus tax where applicable`,
      },
      value: props.returnDetails.shippingFee === 0.0 ? FREE_SHIPMENT_VALUE : `-${formatPrice(props.returnDetails.shippingFee)}`,
    }),
    ...addRow(props.returnDetails.batteryFee, {
      rowSeq: 10,
      id: 'ca-battery-fee',
      label: 'CA embedded battery fee',
      tooltip: {
        class: 'shipping-fee-tooltip',
        content: `This fee is required by California law for products with batteries that cannot easily be removed.
          It funds programs for safe recycling and disposal.
          The fee is calculated based on the product price, with a cap per item.`,
      },
      value: formatPrice(props.returnDetails.batteryFee),
    }),
    ...addRow(estimatedCreditResponse.value && showRefundToAndTotal.value, {
      rowSeq: 11,
      label: returnStatusCode === 303 ? 'Total refund' : estimatedCreditMessage,
      value: formatPrice(estimatedCreditAmount),
      valueClass: isReturnConfirmationPage.value && 'large',
    }),
    {
      rowSeq: 12,
      label: 'Order #',
      value: orderNumber.value,
      screenReaderValue: makeNumberAccessible(orderNumber.value),
    },
    ...addRow(reservationNumber, {
      rowSeq: 13,
      label: 'Reservation #',
      value: reservationNumber,
      screenReaderValue: makeNumberAccessible(reservationNumber),
    }),
    ...addRow(!isReturnConfirmationPage.value, {
      rowSeq: 14,
      label: 'Return ID',
      value: pendingReturnId,
    }),
  ];
});
</script>

<style lang="scss" scoped>
.custom-padding {
  padding-top: rem-calc(5);
}
</style>

<style lang="scss" scoped brand="mcom">
@import '~@core/csg/src/scss/encodedIcons/mcom/_tooltip-small.scss';
</style>
<style lang="scss" scoped brand="bcom">
@import '~@core/csg/src/scss/encodedIcons/bcom/_tooltip-small.scss';
</style>
